import * as atlas from "azure-maps-control";

import type {AzureBaseMap} from "./AzureBaseMap";
import type {PopupOptions} from "azure-maps-control";

export type AddParams = {
    content: JQuery<HTMLElement>,
    popupSize: [number, number],
    location: [number, number],
    offsetPx?: [number, number],
    addCloseBtn? : boolean,
    closeBtnSelector?: string,
    targetBounds?: [number, number, number, number], // todo: not used?
    targetPadding?: [number, number, number, number] // todo: not used?
}

export class AzurePopup {

    private readonly baseMap: AzureBaseMap;
    private readonly map: atlas.Map;
    private readonly eventChannel: any;
    private popups: {[id: number]: atlas.Popup} = {};
    private sequence = 0;

    constructor(map: AzureBaseMap, eventChannel: any) {
        this.baseMap = map;
        this.map = map.map;
        this.eventChannel = eventChannel;
    }

    public add(params: AddParams) {
        const popupId = ++this.sequence;

        const root = $("<div/>");
        const bubble = $("<div/>")
            .addClass("es-map-popup-bubble")
            .css({
                margin: 0,
                boxSizing: "border-box",
                width: params.popupSize[0],
                height: params.popupSize[1]
            });

        const subBubble = $("<div/>").addClass("es-map-popup-subbubble");
        subBubble.appendTo(bubble);
        bubble.appendTo(root)

        if (!params.onClose) {
            params.onClose = () => {
                this.remove(popupId);
            };
        }

        if (params.addCloseBtn !== false) {
            $("<div/>", {"class" : "es-map-popup-closeBtn"})
                .attr("title", "Close")
                .attr("id", "es-select-multi-close-x")
                .on("click", params.onClose)
                .appendTo(subBubble);
        }

        if (typeof params.content === "string") {
            subBubble.append($("<div/>").addClass("es-map-popup-content").html(params.content));
        } else {
            subBubble.append($("<div/>").addClass("es-map-popup-content").append($(params.content)));
        }

        const popup = new atlas.Popup({
            anchor: "top-left", // MapLibreGL option, not documented on Azure, Top Left to avoid popup to be blurry
            fillColor: "transparent",
            content: root[0],
            position: params.location,
            // An array of [pixelsRight, pixelsDown] for how many pixels to the right and down the anchor of the popup should be offset. Negative numbers can be used to offset the popup left and up. default [0, 0]
            pixelOffset: [-params.popupSize[0] / 2, -26],
            closeButton: false,
            showPointer: false
        } as PopupOptions);

        // Workaround to avoid popup to be blurry
        const cont = popup.getPopupContainer();
        $(cont).css("bottom", esApp.map.baseMap.getSizePx()[1]+"px")

        popup.open(this.map);

        this.popups[popupId] = popup;
        return popupId;
    }

    public remove(popupId: number) {
        const popup = this.popups[popupId];
        if (popup) {
            popup.remove();
            delete this.popups[popupId];
            this.eventChannel.fireEvent("onRemovePopup", popupId);
            return true;
        } else {
            return false;
        }
    }

    /**
     * Close all popups.
     * e.g. esApp.map.popup.clear();
     */
    public clear() {
        Object.keys(this.popups).forEach((popupId) => {
            const popup = this.popups[+popupId];
            if (popup) {
                popup.remove();
                delete this.popups[+popupId];
                this.eventChannel.fireEvent("onRemovePopup", +popupId);
            }
        });
    }

    public getContentElement(popupId: number) {
        const popup = this.popups[popupId] as atlas.Popup;
        return $(popup.getPopupContainer()).find(".es-map-popup-bubble");
    }

    public getBounds(popupId: number){
        const popup = this.popups[popupId] as atlas.Popup;
        if (popup) {
            const pixelBounds = this.getPixelBounds(popupId);
            // [west, south, east, north]
            let latLonBounds = this.baseMap.fromPx(pixelBounds);
            latLonBounds = es.util.bounds.sanitize(latLonBounds);
            if (es.debugShowOverlayOnSetBounds) {
                esApp.map.overlay.drawBounds(latLonBounds, undefined, 5000);
            }
            es.log.log("getBounds for popup", popupId, "pixelBounds", pixelBounds, "latLonBounds", latLonBounds);
            return latLonBounds;
        }
    }

    public getPixelBounds(popupId: number){
        const popup = this.popups[popupId] as atlas.Popup;
        if (popup) {
            const div = (popup.getPopupContainer().firstChild as HTMLElement).getBoundingClientRect().toJSON();

            // bit of a hack to use the overall parent div height rather than just the azure popup part
            div.height = parseInt($(".popup-content-container").css("height"));

            const locPx = this.baseMap.toPx(popup.getOptions().position ?? [0, 0]);
            const size  = [
                div.width,
                div.height + 110 // (es.isEmbeddedInSFDC ? 90 : 90)
            ];

            const pixelOffset = popup.getOptions().pixelOffset ?? [0, 0];
            if (popup.getOptions().pixelOffset){
                locPx[0] += pixelOffset[0];
                locPx[1] -= pixelOffset[1];
            }

            let pixelBounds: number[] = [
                locPx[0],
                locPx[1],
                locPx[0] + size[0],
                locPx[1] - size[1]
            ];

            pixelBounds = es.util.bounds.sanitize(pixelBounds);

            if (es.debugShowOverlayOnSetBounds) {
                let latLonBounds = this.baseMap.fromPx(pixelBounds);
                latLonBounds = es.util.bounds.sanitize(latLonBounds);
                esApp.map.overlay.drawBounds(latLonBounds, undefined, 5000);
                es.log.log("getPixelBounds for popup", popupId, "pixelBounds", pixelBounds);
            }

            return pixelBounds;
        }
    }

    public listIds() {
        return Object.keys(this.popups);
    }

    public movePopup(popupId: number, x: number, y: number) {
        const popup = this.popups[popupId];
        if (popup) {
            popup.setOptions({position: [x,y]});
        }
    }

    public hide(popupId: number) {
        const popup = this.popups[popupId];
        if (popup) {
            popup.close();
        }
    }

    public show(popupId: number) {
        const popup = this.popups[popupId];
        if (popup) {
            popup.open(this.map);
        }
    }
}
